import { APAging, ARAging } from "components/Reports/AgingSummary";
import { InteractiveReport } from "components/Reports/InteractiveReport";
import { VendorSummary } from "components/Reports/VendorSummary/VendorSummary";
import {
  BALANCE_SHEET,
  CASH_FLOW_STATEMENT,
  GENERAL_LEDGER,
  INCOME_STATEMENT,
  SAVED_REPORTS,
  TRIAL_BALANCE,
} from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePageTitle } from "hooks/usePageTitle";
import { useEffect } from "react";
import { Route, Switch, useRouteMatch } from "react-router-dom";
import { useReportsPrefetch } from "store/apis/reports";
import { FinancialSummary } from "../FinancialsSummary/FinancialSummary";
import { CustomReports } from "components/Reports/CustomReports/CustomReports";
import { CustomReportViewOnly } from "components/Reports/CustomReports/CustomReportViewOnly";
import { NewCustomReportTemplate } from "../Settings/NewCustomReportTemplate";
import { OthersLanding } from "./OthersLanding";
import { BusinessReviewPage } from "./BusinessReview";

export type ReportsType =
  | typeof BALANCE_SHEET
  | typeof INCOME_STATEMENT
  | typeof CASH_FLOW_STATEMENT
  | typeof SAVED_REPORTS;

export type GeneralLedgerReportType =
  | typeof GENERAL_LEDGER
  | typeof TRIAL_BALANCE;

export const Reports = () => {
  usePageTitle("Reports");
  const entityId = useCurrentEntityId();
  const groupId = useCurrentGroupContext()?.uuid;
  const { path } = useRouteMatch();

  const prefetchLiveReports = useReportsPrefetch("newInteractiveReport");

  useEffect(() => {
    if (entityId && groupId) {
      prefetchLiveReports({
        groupId,
        entityId,
        reportType: "INCOME_STATEMENT",
        accountingMethod: "CASH",
        startDate: dayjs().startOf("year").format(YYYY_MM_DD),
        endDate: dayjs().format(YYYY_MM_DD),
        // @ts-ignore
        showNonZeroAccounts: true,
        comparison_report_type: "TOTALS",
      });

      prefetchLiveReports({
        groupId,
        entityId,
        reportType: "CASH_FLOW",
        accountingMethod: "ACCRUAL",
        startDate: dayjs().startOf("year").format(YYYY_MM_DD),
        endDate: dayjs().format(YYYY_MM_DD),
        // @ts-ignore
        showNonZeroAccounts: true,
        comparison_report_type: "TOTALS",
      });
    }
  }, [entityId, groupId]);

  return (
    <div className="t-h-full t-w-full">
      <Switch>
        <Route exact path={`${path}`}>
          <FinancialSummary />
        </Route>
        <Route exact path={`${path}/balance-sheet`}>
          <InteractiveReport reportType={BALANCE_SHEET} entityId={entityId} />
        </Route>
        <Route exact path={`${path}/income-statement`}>
          <InteractiveReport
            reportType={INCOME_STATEMENT}
            entityId={entityId}
          />
        </Route>
        <Route exact path={`${path}/cash-flow-statement`}>
          <InteractiveReport
            reportType={CASH_FLOW_STATEMENT}
            entityId={entityId}
          />
        </Route>
        <Route exact path={`${path}/others`}>
          <OthersLanding />
        </Route>
        <Route exact path={`${path}/aging-summary`}>
          <ARAging />
        </Route>
        <Route exact path={`${path}/ap-aging`}>
          <APAging />
        </Route>
        <Route exact path={`${path}/vendor-summary`}>
          <VendorSummary />
        </Route>
        <Route exact path={`${path}/custom-reports`}>
          <CustomReports />
        </Route>
        <Route exact path={`${path}/custom-report/new`}>
          <NewCustomReportTemplate />
        </Route>
        <Route path={`${path}/custom-report/:reportId`}>
          <CustomReportViewOnly />
        </Route>
        <Route exact path={`${path}/business-review`}>
          <BusinessReviewPage />
        </Route>
      </Switch>
    </div>
  );
};
